import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, TrendingUp, CreditCard, Clock, Download, Search, RefreshCcw, Lock, LogOut, MessageSquare, X } from 'lucide-react';

import { API } from '../lib/api';
const TOKEN_KEY = 'gynovate_admin_token';
const DEFAULT_TOKEN = 'gynovate-admin-2026';

function formatDate(iso) {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function toCSV(rows) {
  if (!rows?.length) return '';
  const keys = Array.from(
    rows.reduce((s, r) => { Object.keys(r).forEach((k) => s.add(k)); return s; }, new Set())
  );
  const head = keys.join(',');
  const esc = (v) => {
    if (v === null || v === undefined) return '';
    const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const body = rows.map((r) => keys.map((k) => esc(r[k])).join(',')).join('\n');
  return head + '\n' + body;
}

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [tokenInput, setTokenInput] = useState('');
  const [stats, setStats] = useState(null);
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  // Questions modal state
  const [qOpen, setQOpen] = useState(false);
  const [qLoading, setQLoading] = useState(false);
  const [qData, setQData] = useState(null);
  const [qDoctor, setQDoctor] = useState(null);

  const viewQuestions = async (signup) => {
    setQOpen(true);
    setQDoctor(signup);
    setQLoading(true);
    setQData(null);
    try {
      const { data } = await axios.get(
        `${API}/api/admin/messages/${encodeURIComponent(signup.phone)}`,
        { headers: { 'x-admin-token': token } }
      );
      setQData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load questions');
      setQOpen(false);
    } finally {
      setQLoading(false);
    }
  };

  const load = useCallback(async (authToken) => {
    const t = authToken || token;
    if (!t) return;
    setLoading(true);
    try {
      const headers = { 'x-admin-token': t };
      const [sRes, gRes] = await Promise.all([
        axios.get(`${API}/api/admin/stats`, { headers }),
        axios.get(`${API}/api/signups`, { headers }),
      ]);
      setStats(sRes.data);
      setSignups(gRes.data.signups || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error('Invalid admin token');
        localStorage.removeItem(TOKEN_KEY);
        setToken('');
      } else {
        toast.error(err?.message || 'Could not load admin data');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const handleLogin = (e) => {
    e.preventDefault();
    const t = (tokenInput || '').trim();
    if (!t) return;
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setStats(null);
    setSignups([]);
  };

  const filtered = signups.filter((s) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (s.name || '').toLowerCase().includes(q) ||
      (s.email || '').toLowerCase().includes(q) ||
      (s.phone || '').toLowerCase().includes(q) ||
      (s.referral_code || '').toLowerCase().includes(q) ||
      (s.plan_status || '').toLowerCase().includes(q)
    );
  });

  const downloadCSV = () => {
    const csv = toCSV(filtered);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gynovate-signups-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${filtered.length} signups`);
  };

  // ---------- LOGIN SCREEN ----------
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-slate-900 via-[#1a0a10] to-slate-900">
        <form
          onSubmit={handleLogin}
          className="w-full max-w-md rounded-3xl bg-white shadow-2xl p-8"
          data-testid="admin-login-form"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-raspberry-600 flex items-center justify-center">
              <Lock className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Gynovate Admin</h1>
              <p className="text-xs text-slate-500">Signup & subscription data</p>
            </div>
          </div>
          <label className="block text-xs font-semibold text-slate-700 mb-1.5">Admin Token</label>
          <input
            type="password"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            placeholder="Enter admin token"
            autoComplete="off"
            className="w-full px-3 py-2.5 rounded-lg border border-slate-300 text-sm focus:border-raspberry-500 focus:ring-1 focus:ring-raspberry-500 outline-none"
            data-testid="admin-token-input"
          />
          <p className="text-[11px] text-slate-400 mt-2">
            Default token: <code className="bg-slate-100 px-1 rounded">{DEFAULT_TOKEN}</code>
            &nbsp;· Change via <code className="bg-slate-100 px-1 rounded">ADMIN_TOKEN</code> env var.
          </p>
          <button
            type="submit"
            className="mt-5 w-full py-2.5 rounded-lg bg-raspberry-700 hover:bg-raspberry-800 text-white text-sm font-bold transition-colors"
            data-testid="admin-login-btn"
          >
            Unlock Dashboard
          </button>
        </form>
      </div>
    );
  }

  // ---------- DASHBOARD ----------
  const statCards = stats ? [
    { label: 'Total Signups', value: stats.total_signups, icon: Users, color: 'bg-blue-600' },
    { label: 'Last 24 Hours', value: stats.signups_last_24h, icon: Clock, color: 'bg-emerald-600' },
    { label: 'Active Plans', value: stats.active_subscriptions, icon: TrendingUp, color: 'bg-raspberry-700' },
    { label: `Revenue (INR)`, value: `₹${(stats.total_revenue_inr || 0).toLocaleString('en-IN')}`, icon: CreditCard, color: 'bg-amber-600' },
  ] : [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-raspberry-700 flex items-center justify-center">
              <Lock className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold text-slate-900">Gynovate Admin</h1>
              <p className="text-[11px] text-slate-500">Signup & subscription data · MongoDB: <code className="bg-slate-100 px-1 rounded">signups</code> collection</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => load()}
              disabled={loading}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-700 border border-slate-300 hover:bg-slate-100 flex items-center gap-1.5 disabled:opacity-50"
              data-testid="admin-refresh-btn"
            >
              <RefreshCcw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
            <button
              onClick={handleLogout}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-100 flex items-center gap-1.5"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="bg-white rounded-2xl border border-slate-200 p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{s.label}</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{s.value ?? '-'}</p>
                  </div>
                  <div className={`w-9 h-9 rounded-lg ${s.color} flex items-center justify-center`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Signups table */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-bold text-slate-900">Signups ({filtered.length})</h2>
              <p className="text-xs text-slate-500">Latest first · stored in MongoDB → <code>signups</code></p>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name / phone / email"
                  className="pl-8 pr-3 py-1.5 rounded-lg border border-slate-300 text-xs w-56 focus:border-raspberry-500 focus:ring-1 focus:ring-raspberry-500 outline-none"
                  data-testid="admin-search"
                />
              </div>
              <button
                onClick={downloadCSV}
                disabled={!filtered.length}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-emerald-700 hover:bg-emerald-800 flex items-center gap-1.5 disabled:opacity-50"
                data-testid="admin-csv-btn"
              >
                <Download className="w-3.5 h-3.5" /> Export CSV
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-left">
                <tr>
                  {['Name', 'Email', 'Phone', 'Country', 'Plan', 'Status', 'Expires', 'Ref Code', 'Referrals', 'Lifetime Paid', 'Joined', 'Questions'].map((h) => (
                    <th key={h} className="px-3 py-2.5 font-semibold text-slate-600 uppercase tracking-wide text-[10px]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((s, i) => (
                  <tr key={s.phone || s.email || i} className="hover:bg-slate-50">
                    <td className="px-3 py-2.5 font-medium text-slate-900 whitespace-nowrap">{s.name || '-'}</td>
                    <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">{s.email || '-'}</td>
                    <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">{s.phone || '-'}</td>
                    <td className="px-3 py-2.5 text-slate-600">{s.country || '-'}</td>
                    <td className="px-3 py-2.5 text-slate-600">{s.plan || 'free'}</td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${
                        s.plan_status === 'active' ? 'bg-emerald-100 text-emerald-800' :
                        s.plan_status === 'expired' ? 'bg-red-100 text-red-800' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {s.plan_status || 'free'}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{formatDate(s.plan_expires_at)}</td>
                    <td className="px-3 py-2.5 font-mono text-[11px] text-raspberry-700">{s.referral_code || '-'}</td>
                    <td className="px-3 py-2.5 text-slate-700 text-center">{s.referral_count ?? 0}</td>
                    <td className="px-3 py-2.5 text-slate-900 font-semibold">
                      {s.lifetime_paid_inr ? `₹${s.lifetime_paid_inr.toLocaleString('en-IN')}` : '-'}
                    </td>
                    <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{formatDate(s.created_at)}</td>
                    <td className="px-3 py-2.5">
                      <button
                        onClick={() => viewQuestions(s)}
                        disabled={!s.phone}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold text-raspberry-700 bg-raspberry-50 border border-raspberry-200 hover:bg-raspberry-100 disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid={`view-questions-${s.phone || i}`}
                        title="View this doctor's chat questions"
                      >
                        <MessageSquare className="w-3 h-3" /> View
                      </button>
                    </td>
                  </tr>
                ))}
                {!filtered.length && (
                  <tr><td colSpan={12} className="text-center py-10 text-slate-400 text-sm">
                    {loading ? 'Loading…' : 'No signups match your search.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Data storage note */}
        <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-5">
          <h3 className="text-sm font-bold text-slate-900 mb-2">Where is signup data stored?</h3>
          <ul className="space-y-1.5 text-xs text-slate-600">
            <li>• <strong>Database:</strong> MongoDB — database name comes from <code className="bg-slate-100 px-1 rounded">DB_NAME</code> env var (default <code className="bg-slate-100 px-1 rounded">gynovate_db</code>).</li>
            <li>• <strong>Collection:</strong> <code className="bg-slate-100 px-1 rounded">signups</code></li>
            <li>• <strong>Schema:</strong> name, email, phone, country, plan, plan_status, plan_expires_at, referral_code, referral_count, referred_by, created_at.</li>
            <li>• <strong>Chat sessions:</strong> collections <code className="bg-slate-100 px-1 rounded">sessions</code> + <code className="bg-slate-100 px-1 rounded">messages</code> (unsaved sessions auto-purge after 24 h for HIPAA).</li>
            <li>• <strong>Payments:</strong> collection <code className="bg-slate-100 px-1 rounded">transactions</code>.</li>
            <li>• <strong>Direct access:</strong> MongoDB Atlas → connect using the <code className="bg-slate-100 px-1 rounded">MONGO_URL</code> env value. Or use Compass / mongosh with the same URI.</li>
          </ul>
        </div>
      </main>

      {/* Questions Modal */}
      {qOpen && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setQOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-5 border-b border-slate-200">
              <div className="min-w-0">
                <p className="text-[11px] font-bold tracking-widest uppercase text-raspberry-700 mb-1">Doctor Questions</p>
                <h3 className="text-lg font-bold text-slate-900 truncate">{qDoctor?.name || qDoctor?.phone || 'Doctor'}</h3>
                <p className="text-xs text-slate-500 truncate">{qDoctor?.email || ''} · {qDoctor?.phone || ''}</p>
                {qData && (
                  <p className="mt-1 text-xs text-slate-600">
                    <span className="font-semibold text-slate-900">{qData.question_count}</span> questions across{' '}
                    <span className="font-semibold text-slate-900">{qData.session_count}</span> sessions
                  </p>
                )}
              </div>
              <button
                onClick={() => setQOpen(false)}
                className="text-slate-500 hover:text-slate-900 p-1 -mr-1 rounded-lg hover:bg-slate-100"
                data-testid="close-questions-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {qLoading && (
                <div className="text-center py-10 text-sm text-slate-500">Loading questions…</div>
              )}
              {!qLoading && qData && qData.questions?.length === 0 && (
                <div className="text-center py-10 text-sm text-slate-500">
                  This doctor hasn&apos;t asked any questions yet.
                </div>
              )}
              {!qLoading && qData && qData.questions?.length > 0 && (
                <ul className="space-y-3">
                  {qData.questions.map((q, i) => (
                    <li key={i} className="rounded-xl border border-slate-200 bg-slate-50 p-3.5 hover:border-raspberry-200 hover:bg-white transition-colors">
                      <div className="flex items-center gap-2 mb-1.5 text-[10px] uppercase tracking-wider">
                        {q.mode && (
                          <span className="font-bold text-raspberry-700 bg-raspberry-50 px-1.5 py-0.5 rounded border border-raspberry-200">{q.mode}</span>
                        )}
                        {q.language && (
                          <span className="font-semibold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{q.language}</span>
                        )}
                        <span className="ml-auto text-slate-500 font-medium">{formatDate(q.created_at)}</span>
                      </div>
                      <p className="text-sm text-slate-900 whitespace-pre-wrap leading-snug">{q.content}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
